/**
 * TopBar — Horizontal bar at the very top of the app
 *
 * Left: ArgusAI logo + name
 * Center: Breadcrumbs (section > page)
 * Right: Global actions (theme toggle, notifications, user)
 */

import { useLocation, Link, useNavigate } from '@tanstack/react-router';
import { Sun, Moon, Bell, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/components/theme-provider';
import { RAIL_ITEMS } from '@/components/nav-rail';
import { useAuth } from '@/contexts/auth-context';

// Map paths to breadcrumb labels
const PATH_LABELS: Record<string, string> = {
  '/ask-argus': 'Ask Argus',
  '/approvals': 'Approvals',
  '/workspace': 'Workspace',
  '/workspace/tools': 'Tools',
  '/workspace/skills': 'Skills',
  '/workspace/prompts': 'Prompts',
  '/workspace/connections': 'Connections',
  '/library': 'Library',
  '/library/search': 'Search',
  '/library/evaluation': 'Evaluation',
  '/automate': 'Automate',
  '/automate/triggers': 'Triggers',
  '/automate/workflows': 'Workflows',
  '/automate/connections': 'Connections',
  '/models': 'Models',
  '/insights': 'Insights',
  '/insights/reports': 'Reports',
  '/insights/notifications': 'Notifications',
  '/insights/sla': 'SLA',
  '/trust': 'Trust',
  '/trust/activity': 'Activity Trail',
  '/trust/violations': 'Violations',
  '/trust/data-flows': 'Data Flows',
  '/trust/ai-systems': 'AI Systems',
  '/trust/frameworks': 'Frameworks',
  '/trust/evidence': 'Evidence Vault',
  '/trust/audit': 'Audit Log',
  '/admin': 'Admin',
  '/admin/users': 'Users',
  '/admin/security': 'Security',
  '/admin/branding': 'Branding',
  '/admin/health': 'Health',
  '/admin/settings': 'Settings',
  '/admin/notifications': 'Notifications',
  '/playground': 'Playground',
};

function getBreadcrumbs(pathname: string): Array<{ label: string; path: string }> {
  const crumbs: Array<{ label: string; path: string }> = [];

  // Find the rail item this path belongs to
  const railItem = RAIL_ITEMS.find(item =>
    pathname === item.path || pathname.startsWith(item.path + '/')
  );

  if (railItem) {
    crumbs.push({ label: railItem.label, path: railItem.path });
  }

  // If we're on a sub-page, add the sub-page
  if (pathname !== railItem?.path && PATH_LABELS[pathname]) {
    crumbs.push({ label: PATH_LABELS[pathname], path: pathname });
  }

  return crumbs;
}

export function TopBar() {
  const { mode, toggleTheme } = useTheme();
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);
  const navigate = useNavigate();
  const { user, isSysadmin, signOut } = useAuth();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const userEmail = user?.email || '—';
  const userName = fullName || user?.email || 'User';
  const userInitials = (fullName || user?.email || 'U').split(/[\s@]/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || 'U';
  // Issue #87 — delegate to the AuthProvider's signOut, which:
  //   (1) revokes the refresh token server-side via POST /auth/logout
  //   (2) clears localStorage tokens
  //   (3) resets the AuthContext React state (so protected UI hides
  //       immediately, not just after the next navigation)
  //   (4) drops the TanStack Query cache
  //   (5) navigates to /login with history replacement.
  // The previous implementation only did step (2), which left protected
  // content visible and the server-side session alive.
  const handleSignOut = () => {
    void signOut();
  };

  return (
    <div className="h-12 border-b border-border/40 bg-background flex items-center px-4 shrink-0">
      {/* Left: Logo + Name — width matches the rail (76px) + natural text width */}
      <Link to="/" className="flex items-center gap-3 shrink-0 min-w-[76px]">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
          <span className="text-primary-foreground text-base font-bold">A</span>
        </div>
        <span className="text-base font-semibold tracking-tight whitespace-nowrap">ArgusAI</span>
      </Link>

      {/* Divider between logo and breadcrumbs */}
      <div className="h-5 w-px bg-muted-foreground/30 mx-5 shrink-0" />

      {/* Center: Breadcrumbs */}
      <div className="flex-1 flex items-center gap-1.5 text-[13px]">
        {breadcrumbs.map((crumb, i) => (
          <div key={crumb.path} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/40" />}
            {i === breadcrumbs.length - 1 ? (
              <span className="font-medium text-muted-foreground">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="text-muted-foreground/60 hover:text-foreground transition-colors">
                {crumb.label}
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Right: Global actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Theme toggle */}
        <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8 rounded-lg" aria-label={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} title={mode === 'dark' ? 'Light theme' : 'Dark theme'}>
          {mode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* Notifications — bug #26: the bell had a fake unread dot and NO
            onClick (clicking did nothing). Wire it to a real dropdown with an
            honest empty state + a shortcut to pending approvals; drop the
            fabricated unread indicator. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg relative" aria-label="Notifications" title="Notifications">
              <Bell className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <div className="px-2 py-1.5 text-sm font-medium">Notifications</div>
            <DropdownMenuSeparator />
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              You're all caught up — no new notifications.
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate({ to: '/approvals' })}>
              View pending approvals
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User avatar */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label="User menu" title="Account">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-semibold text-primary">
                {userInitials}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{userName}</p>
              <p className="text-xs text-muted-foreground">{userEmail}</p>
            </div>
            <DropdownMenuSeparator />
            {/*
              Issue #560 — when the active token is sysadmin
              (audience: argusai-admin), the user-menu Settings
              entry MUST route to `/sysadmin/settings` instead of
              the tenant-scoped `/admin` surface. Mixing the two
              produced a sysadmin rail rendering over tenant-admin
              content, which is a control-plane UX/security
              boundary regression.
            */}
            <DropdownMenuItem
              onClick={() =>
                navigate({ to: isSysadmin ? '/sysadmin/settings' : '/admin' })
              }
            >
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={handleSignOut}>Sign Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
